import React from 'react';
import moment, { Moment } from 'moment';
import { Tooltip } from '@material-ui/core';
import { formatInTimeZone } from 'date-fns-tz';
import { convertTokens } from '@date-fns/upgrade/v2';
import FormLabel from '@material-ui/core/FormLabel';

import './style.css';

import FormDateSelector from 'retired/FormDateSelector';
import { gray2 } from 'utils/color-styles';
import { TimeZonePicker } from 'components/TimeZone/TimeZonePicker';

interface DateInput {
  value: Date;
  onChange: (value: Date | null) => void;
}

interface Props {
  startingAtInput: DateInput;
  endingAtInput: DateInput;
  allowClear?: boolean;
  disabled?: boolean;
  error?: boolean;
  hideDuration?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isPreviousDateDisabled?: boolean;
  isNextDateDisabled?: boolean;
  displayTimeAndTimeZone?: boolean;
  timeZoneDisabled?: boolean;
  timeZone?: string;
  setTimeZone?: (timeZone: string) => void;
  defaults?: [Date, Date] | [];
}

/**
 * This is basically a fork of `FormDateWithStartAndEndTime`, but agnostic to redux-form
 */
export const DateWithStartAndEndTime = ({
  startingAtInput,
  endingAtInput,
  error,
  hideDuration,
  defaults = [],
  minDate,
  maxDate,
  isPreviousDateDisabled,
  isNextDateDisabled,
  disabled,
  displayTimeAndTimeZone,
  timeZone,
  setTimeZone,
  timeZoneDisabled,
  allowClear,
}: Props) => {
  const [passedDefaultStartingAt, passedDefaultEndingAt] = defaults;
  const startingAtMoment = startingAtInput.value ? moment(startingAtInput.value) : null;
  const endingAtMoment = endingAtInput.value ? moment(endingAtInput.value) : null;
  const minMoment = minDate ? moment(minDate) : null;
  const maxMoment = maxDate ? moment(maxDate) : null;
  const defaultStartingAt = passedDefaultStartingAt ? moment(passedDefaultStartingAt) : null;
  const defaultEndingAt = passedDefaultEndingAt ? moment(passedDefaultEndingAt) : null;

  const duration =
    (startingAtMoment &&
      endingAtMoment &&
      moment.duration(startingAtMoment.diff(endingAtMoment))) ??
    null;

  const getTimeZone = () =>
    timeZone || (typeof Intl === 'object' && Intl.DateTimeFormat()?.resolvedOptions()?.timeZone);
  const getUTC = () =>
    timeZone
      ? formatInTimeZone(
          startingAtMoment ? startingAtMoment.toDate() : Date.now(),
          timeZone,
          convertTokens('Z')
        )
      : moment().format('Z');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
        <FormDateSelector
          input={{
            value: startingAtMoment,
            onChange: (newValue: Moment) => {
              if (newValue) {
                startingAtInput.onChange(newValue.toDate());
                const defaultNewEndingAtMoment = newValue.clone().add(2, 'hours');
                let newEndingAtMoment = endingAtMoment ?? defaultNewEndingAtMoment;

                if (Math.abs(newValue.diff(newEndingAtMoment, 'hours', true)) > 24) {
                  newEndingAtMoment = newEndingAtMoment.clone().set({
                    year: newValue.get('year'),
                    month: newValue.get('month'),
                    date: newValue.get('date'),
                  });
                }

                if (endingAtMoment && newValue.isSameOrAfter(newEndingAtMoment)) {
                  newEndingAtMoment = defaultNewEndingAtMoment;
                }

                if (maxMoment && newEndingAtMoment.isAfter(maxMoment)) {
                  newEndingAtMoment = maxMoment.clone();
                }

                if (endingAtMoment && newEndingAtMoment.isSame(endingAtMoment)) {
                  return;
                }

                endingAtInput.onChange(newEndingAtMoment.toDate());
                return;
              }

              if (defaultStartingAt) {
                setTimeout(() => endingAtInput.onChange(defaultStartingAt.toDate()), 0);
                return;
              }

              endingAtInput.onChange(null);
            },
          }}
          disabled={disabled}
          noDate={isPreviousDateDisabled && isNextDateDisabled}
          placeholder="Date *"
          isPreviousDateDisabled={isPreviousDateDisabled}
          isNextDateDisabled={isNextDateDisabled}
          placeholderTime="Start Time *"
          defaultValue={defaultStartingAt}
          minDate={minMoment ?? moment().startOf('day')}
          maxDate={maxMoment ?? null}
          allowClear={allowClear}
        />
        <FormDateSelector
          input={{
            value: endingAtMoment,
            onChange: (newValue: Moment) => {
              if (newValue) {
                const value = startingAtMoment ? startingAtMoment.clone() : moment();
                value.hour(newValue.hour());
                value.minutes(newValue.minutes());
                setTimeout(() => endingAtInput.onChange(value.toDate()), 0);
                return;
              }
              if (defaultEndingAt) {
                setTimeout(() => endingAtInput.onChange(defaultEndingAt.toDate()), 0);
              }
            },
          }}
          disabled={disabled}
          noDate
          placeholder="End Time *"
          defaultValue={defaultEndingAt}
          minDate={startingAtMoment ?? minMoment ?? null}
          maxDate={maxMoment ?? null}
          allowClear={allowClear}
        />

        {timeZone && setTimeZone && (
          <TimeZonePicker
            timeZone={timeZone}
            setTimeZone={setTimeZone}
            disabled={timeZoneDisabled}
          />
        )}
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: 40,
              alignItems: 'center',
            }}
          >
            {displayTimeAndTimeZone && (
              <Tooltip title={getTimeZone()}>
                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11,
                    color: gray2,
                    width: 70,
                  }}
                >
                  UTC{getUTC()}
                </div>
              </Tooltip>
            )}
            {!hideDuration && duration && !setTimeZone && (
              <div
                style={{
                  marginTop: 5,
                  marginLeft: 2,
                  fontSize: 11,
                  color: gray2,
                  width: 100,
                }}
              >
                {duration.humanize()}
              </div>
            )}
          </div>
        </div>
      </div>
      {error && (
        <FormLabel error style={{ fontSize: 12, marginTop: 10 }}>
          Required
        </FormLabel>
      )}
    </>
  );
};
